import { useMemo } from "react"
import { alpha, Box, Skeleton, useTheme } from "@mui/material"
import { DateTime } from "luxon"

import type { Device } from "types/device.types"

import useOrgDeviceStatuses from "helpers/hooks/devices/useOrgDeviceStatuses"
import { getDeviceReportingStatus } from "helpers/utils/devices"
import ReportingStatus from "./ReportingStatus"

interface StickyReportingStatusProps {
  device: Device
}

const StickyReportingStatus = ({ device }: StickyReportingStatusProps) => {
  const theme = useTheme()
  const { statusesById, isLoading } = useOrgDeviceStatuses()

  const reportingStatus = useMemo(() => {
    const status = statusesById[device.id]
    const now = DateTime.now()
    return getDeviceReportingStatus(status, now)
  }, [device, statusesById])

  return isLoading ? (
    <Box
      position={"sticky"}
      top={0}
      // Below the modal layer (theme.zIndex.modal === 1300) so drawers, dialogs
      // and menus render on top of the banner, but above page content.
      zIndex={theme.zIndex.appBar}
      borderRadius={"0 0 4px 4px"}
      mt={"-20px"}
      mb={2}
    >
      <Skeleton variant="rounded" width={"100%"} height={36} />
    </Box>
  ) : (
    <Box
      position={"sticky"}
      top={0}
      // Below the modal layer (theme.zIndex.modal === 1300) so drawers, dialogs
      // and menus render on top of the banner, but above page content.
      zIndex={theme.zIndex.appBar}
      borderBottom={1}
      borderLeft={1}
      borderRight={1}
      borderColor={theme.palette.deviceStatus[reportingStatus].main}
      borderRadius={"0 0 4px 4px"}
      bgcolor={theme.palette.background.paper}
      minHeight={30}
      mt={"-20px"}
      mb={2}
    >
      <Box
        width={"100%"}
        minHeight={30}
        bgcolor={alpha(theme.palette.deviceStatus[reportingStatus].light, 0.1)}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
        p={1}
      >
        <ReportingStatus status={reportingStatus} variant={"legend"} />
      </Box>
    </Box>
  )
}

export default StickyReportingStatus
